import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { formatLabel, getErrorResponse, getUpdatedSearchParams, parseSearchParams } from 'services/util'
import { twElementStyles } from '../../../../twElementStyles'
import { usePopup } from '../../../../context/popupContext'
import { RenderDefaultActions, RenderStatus, RenderDefaultTemplate } from '../../common'
import { CustomTable } from '../../../../components/customTable'
import { FilterOptions } from '../../../../components/customTable/custom-table-interfaces'
import { useNotificationBar } from '../../../../context/notificationContext'
import { SmoothTransition } from '../../../../components/pageTransition'
import { Unauthorized } from '../../../Unauthorized'
import { PagePreloader } from '../../../../components/pagePreloader'
import { useDeleteTemplate, useGetTemplatesData, useStatusTemplates, useSetDefaultTemplate } from 'hooks/templates'
import { useNavigate } from 'react-router-dom';

export interface Response {
    records: any[],
    pages?: number,
    total?: number,
    pagination?: Object
}
export const TemplateListing = () => {
    let [searchParams, setSearchParams] = useSearchParams()
    let filterParams: any = parseSearchParams(searchParams, 'filter')
    const navigate = useNavigate();
    const notification = useNotificationBar()
    let popup = usePopup()
    let { data, isFetching: isLoading, refetch, isError, isSuccess, error, dataUpdatedAt } = useGetTemplatesData(filterParams, {
        keepPreviousData: true
    })
    let { mutate: updateStatusTemplate } = useStatusTemplates()
    let { mutate: deleteTemplate } = useDeleteTemplate()
    const { mutate: setDefaultTemplate } = useSetDefaultTemplate()

    const [defaultTemplateId, setDefaultTemplateId] = useState<string | null>(null)

    useEffect(() => {
        const currentDefault = tableData.find((t: any) => t.is_default)
        setDefaultTemplateId(currentDefault?.id ?? null)
    }, [dataUpdatedAt])

    let useFormHook = useForm({
        defaultValues: filterParams
    })

    let responseData: any = data?.data;

    // ✅ HANDLE BOTH ARRAY & PAGINATED OBJECT
    const rawList = Array.isArray(responseData)
        ? responseData
        : responseData?.results || [];


    const TEMPLATE_TYPE_LABELS: Record<string, string> = {
        HOME: 'Home',
        CATEGORY: 'Category',
        COLLECTION: 'Collection',
        SHOW: 'Show',
        NEW_TYPE: 'New Template',   // 🔥 ADD THIS
    };

    const tableData = rawList.map((item: any) => {
        const backendIsDefault =
            item.isDefault === 1 || item.isDefault === true

        return {
            ...item,

            template_name: item.name ?? item.template_name ?? '',
            template_type: item.template_type ?? '',

            widgets: Array.isArray(item.data)
                ? item.data
                : Array.isArray(item.widgets)
                    ? item.widgets
                    : [],

            // 🔥 NORMALIZED DEFAULT FLAG
            is_default: backendIsDefault,
        }
    })



    const onDeleteAction = (row: any) => {
        popup.open({
            title: 'Are you sure ?',
            message: (
                <>
                    Do you want to delete template{' '}
                    <strong className="font-semibold">{row?.name}</strong>? This cannot be undone.
                </>
            ),
            type: 'confirm',
            onAction: (value: boolean) => {
                if (value) {
                    deleteTemplate({
                        id: row?.id
                    }, {
                        onSuccess: () => {
                            notification.push({
                                title: `Template deleted !`,
                                message: `Template ${row?.name} has been deleted successfully.`,
                                type: 'info'
                            })
                            refetch();
                        },
                        onError(error) {
                            notification.push(getErrorResponse(error))
                        }
                    })
                }
            },
            confirmButtonText: 'Delete',
            confirmButtonClass: twElementStyles.twButtonElegantRed
        })
    }

    const onDefaultChange = (row: any) => {
        const isCurrentlyDefault = row.is_default

        popup.open({
            title: 'Are you sure?',
            message: isCurrentlyDefault ? (
                <>
                    Do you want to remove{' '}
                    <strong className="font-semibold">{row.template_name}</strong> from
                    default template?
                </>
            ) : (
                <>
                    Do you want to set{' '}
                    <strong className="font-semibold">{row.template_name}</strong> as
                    default template?
                </>
            ),
            type: 'confirm',
            onAction: (value: boolean) => {
                if (!value) return

                setDefaultTemplate(
                    {
                        data: {
                            id: row.id,
                            isDefault: isCurrentlyDefault ? 0 : 1,
                        },
                    },
                    {
                        onSuccess: () => {
                            notification.push({
                                title: 'Success',
                                message: isCurrentlyDefault
                                    ? 'Default template removed'
                                    : `${row.template_name} set as default`,
                                type: isCurrentlyDefault ? 'info' : 'success',
                            })

                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        },
                    }
                )
            },
        })
    }





    const onStatusChange = async (row: any) => {
        let update: any = { status: row.status ? 0 : 1 }
        popup.open({
            title: `Are you sure ?`,
            message: `Do you want to ${update.status ? 'Activate' : 'Deactivate'} ${row.name} ?`,
            type: 'confirm',
            onAction: (value: any) => {
                if (value) {
                    updateStatusTemplate({
                        data: update,
                        id: row.id
                    }, {
                        onSuccess: () => {
                            notification.push({
                                title: `${update.status ? 'activation success !' : 'deactivated !'} `,
                                message: `${row.name} ${update.ststus ? 'activated' : 'deactivated'} successfully !`,
                                type: update.status ? 'success' : 'info'
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        }
                    })
                }
            }
        })

    }

    const onAction = (actionType: any, row: any) => {
        if (actionType === 'edit') {
            navigate(`update-template/${row.id}`);
        }
        if (actionType === 'delete') onDeleteAction(row)
    }

    const renderWidgetChips = (widgets?: any[]) => {

        // ✅ NULL / UNDEFINED SAFE
        if (!Array.isArray(widgets) || widgets.length === 0) {
            return <span className="text-gray-400">—</span>;
        }

        const MAX_VISIBLE = 3;
        const visible = widgets.slice(0, MAX_VISIBLE);
        const hidden = widgets.slice(MAX_VISIBLE);

        return (
            <div className="flex flex-wrap gap-1 max-w-[220px] relative">
                {visible.map((w, index) => (
                    <span
                        key={`${w.widget_id}-${index}`}
                        className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700 whitespace-nowrap dark:text-slate-100 dark:bg-slate-700"
                    >
                        {/* ✅ NEW payload */}
                        {w.widget_name}
                    </span>
                ))}

                {hidden.length > 0 && (
                    <div className="relative group">
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 cursor-default dark:text-slate-100 dark:bg-slate-700">
                            +{hidden.length} more
                        </span>

                        <div className="
                        absolute left-0 top-full mt-1
                        hidden group-hover:block
                        z-50
                        min-w-[180px]
                        max-w-[260px]
                        p-2
                        bg-white
                        border
                        rounded
                        shadow-lg
                        dark:text-slate-100 dark:bg-slate-800
                    ">
                            <div className="flex flex-wrap gap-1">
                                {hidden.map((w, index) => (
                                    <span
                                        key={`${w.widget_id}-hidden-${index}`}
                                        className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 whitespace-nowrap dark:text-slate-100 dark:bg-slate-700"
                                    >
                                        {w.widget_name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };




    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'template_name',
            header: () => 'TEMPLATE NAME',
        },
        {
            accessorKey: 'template_type',
            header: () => 'TEMPLATE TYPE',
            cell: ({ row }) => {
                const type = row.original.template_type;
                // 🔥 SHOW LABEL IF KNOWN, ELSE SHOW RAW BACKEND VALUE
                // return TEMPLATE_TYPE_LABELS[type] ?? type ?? '—';
                return formatLabel(row.original.template_type);
            },
        },
        {
            id: 'widgets',
            header: 'WIDGETS',
            cell: ({ row }) =>
                renderWidgetChips(
                    row.original.widgets || row.original.data || []
                ),
        },

        {
            id: 'default',
            header: 'DEFAULT',
            accessorKey: 'is_default',
            cell: ({ row }) =>
                RenderDefaultTemplate(row, 'is_default', onDefaultChange),
            enableSorting: false,
        },

        {
            id: 'status',
            header: 'STATUS',
            accessorKey: 'status',
            cell: ({ row }) => RenderStatus(row, 'status', onStatusChange),
            enableSorting: false
        },
        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) => RenderDefaultActions(row, ['edit', 'delete'], onAction)
        },

    ]

    const mobileViewOptions = {
        mainTitle: { accessKey: 'template_name' }
    }

    const rowSelectionState = useState<RowSelectionState>({})

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(filterParams, values, name)
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                }
                else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const filterOptions: FilterOptions[] = [{
        key: 'status',
        options: [
            {
                display: `All`,
                value: ''
            },
            {
                display: `Active`,
                value: 1
            },
            {
                display: `Inactive`,
                value: 0
            }
        ],
        label: 'Status'
    },
    ]

    useEffect(() => {
        const handlePrintScreen = (e) => {
            if (e.key === 'PrintScreen') {
                navigator.clipboard.writeText('');
                alert('Screenshots disabled!');
            }
        };

        const handleCtrlP = (e) => {
            if (e.ctrlKey && e.key === 'p') {
                alert('This section is not allowed to print or export to PDF');
                e.preventDefault();
                e.stopPropagation();
            }
        };

        document.addEventListener('keyup', handlePrintScreen);
        document.addEventListener('keydown', handleCtrlP);

        return () => {
            document.removeEventListener('keyup', handlePrintScreen);
            document.removeEventListener('keydown', handleCtrlP);
        };
    }, []);



    return (
        (isError || isSuccess) ?
            <SmoothTransition>
                {
                    (error as any)?.response?.data?.statusCode === 403 ?
                        <Unauthorized /> :
                        <div className='w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md '>
                            {/* <AddUpdateUser isEditForm={editForm} rolesData={rolesData} onSubmitHandler={onDataAddUpdateAction} input_data={addUpdateInput} isOpen={!!addUpdate} setIsOpen={setAddUpdate} /> */}
                            <aside className="flex flex-auto items-center">
                                <CustomTable
                                    title="Template Management"
                                    formHook={useFormHook}
                                    columns={columns}
                                    data={tableData}
                                    pages={responseData?.pages}
                                    totalRecords={responseData?.total}
                                    onRefresh={refetch}
                                    isLoading={isLoading}
                                    isErrored={isError}
                                    rowSelectionState={rowSelectionState}
                                    mobileViewOptions={mobileViewOptions}
                                    useFieldForSelect="id"
                                    filterOptions={filterOptions}
                                    onAddAction={() => {
                                        navigate('/template/template-management/create-template');
                                    }}
                                />

                            </aside>
                        </div>
                }
            </SmoothTransition>
            : <PagePreloader />
    )
}
